import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { ActiveDriversService } from '@app/services/active-drivers.service';
import { ReusableGoogleMapComponent } from '@app/components/reusable-google-map/reusable-google-map.component';

@Component({
  selector: 'app-active-drivers',
  templateUrl: './active-drivers.component.html',
  styleUrls: ['./active-drivers.component.scss']
})
export class ActiveDriversComponent implements OnInit, OnDestroy {

  constructor(
    private dialogRef: MatDialogRef<ActiveDriversComponent>,
    private activeDriversService: ActiveDriversService, private dialog: MatDialog,
  ) { }

  searchTerm = '';
  activeFilter = 'all';
  currentPage = 1;
  itemsPerPage = 10;

  get isLoading() { return this.activeDriversService.isLoading; }

  paginatedDrivers: any[] = [];
  countsData: any = {};
  totalCount = 0;
  activeContactId: string | null = null;
  showCopiedId: string | null = null;

  private sub!: Subscription;

  statusConfig: any = {
    'en-route': { label: 'En Route', class: 'status-enroute' },
    'arrived': { label: 'Arrived', class: 'status-arrived' },
    'shipped': { label: 'Shipped', class: 'status-shipped' },
    'pending': { label: 'Pending', class: 'status-pending' },
    'warehouse': { label: 'Warehouse', class: 'status-warehouse' },
    'successful': { label: 'Successful', class: 'status-successful' },
    'unsuccessful': { label: 'Unsuccessful', class: 'status-unsuccessful' },
    'cancelled': { label: 'Cancelled', class: 'status-cancelled' }
  };

  ngOnInit() {
    this.sub = this.activeDriversService.activeDrivers$.pipe(skip(1)).subscribe((data: any) => {
      this.countsData = data.counts || {};
      this.totalCount = data.count || 0;
      this.paginatedDrivers = data.results || [];
    });
    this.loadDrivers();
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  loadDrivers() {
    this.activeDriversService.getActiveDrivers(
      this.currentPage,
      this.searchTerm,
      this.activeFilter
    );
  }

  // ---- Stats ----
  get totalActive() { return this.countsData?.active_now ?? 0; }
  get totalCompleted() { return this.countsData?.total_completed_deliveries ?? 0; }
  get avgETA() { return this.countsData?.average_eta ?? 0; }
  get TotalOrders() { return this.countsData?.total_orders ?? 0; }

  // ---- Empty State ----
  get emptyStateConfig() {
    if (this.searchTerm) {
      return {
        title: 'No Matching Drivers',
        message: `We couldn't find any drivers matching "${this.searchTerm}". Please try a different name or clear the search.`
      };
    }

    switch (this.activeFilter) {
      case 'en-route':
        return {
          title: 'No Drivers En Route',
          message: 'There are currently no drivers actively delivering orders on the road at this moment.'
        };
      case 'arrived':
        return {
          title: 'No Drivers Arrived',
          message: 'No drivers have reached their delivery destinations yet.'
        };
      case 'shipped':
        return {
          title: 'No Shipped Orders',
          message: 'There are no active drivers with orders in the shipped state currently.'
        };
      case 'pending':
        return {
          title: 'No Pending Drivers',
          message: 'All online drivers currently have their assignments active or already completed.'
        };
      default:
        return {
          title: 'No Drivers Currently Online',
          message: 'Drivers will appear here automatically once they log into the mobile app and begin their shift.'
        };
    }
  }

  // ---- Filter Tabs ----
  get filterTabs() {
    return [
      { key: 'all', label: 'All Drivers', count: this.countsData?.active_now ?? 0 },
      { key: 'en-route', label: 'En Route', count: this.countsData?.enroute ?? 0 },
      { key: 'arrived', label: 'Arrived', count: this.countsData?.arrived ?? 0 },
      { key: 'shipped', label: 'Shipped', count: this.countsData?.shipped ?? 0 },
      { key: 'pending', label: 'Pending', count: this.countsData?.pending ?? 0 }
    ];
  }

  // ---- Pagination ----
  get totalPages() { return Math.ceil(this.totalCount / this.itemsPerPage) || 1; }
  get startItem() { return this.totalCount ? ((this.currentPage - 1) * this.itemsPerPage + 1) : 0; }
  get endItem() { return Math.min(this.currentPage * this.itemsPerPage, this.totalCount); }

  filterDrivers(status: string) {
    this.activeFilter = status;
    this.currentPage = 1;
    this.loadDrivers();
  }

  onSearch() {
    this.currentPage = 1;
    this.loadDrivers();
  }

  clearSearch() {
    this.searchTerm = '';
    this.onSearch();
  }

  changePage(dir: number) {
    this.currentPage += dir;
    this.loadDrivers();
  }

  refreshData() {
    this.currentPage = 1;
    this.loadDrivers();
  }

  closeModal() {
    this.dialogRef.close();
  }

  // ---- Helper Functions ----
  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  }

  getLikeCount(driver: any): number {
    return driver.like?.find((l: any) => l.is_like === true)?.count ?? 0;
  }

  getDriverRating(driver: any): number {
    const likes = driver.like?.find((l: any) => l.is_like === true)?.count || 0;
    const dislikes = driver.like?.find((l: any) => l.is_like === false)?.count || 0;
    const total = likes + dislikes;
    return total > 0 ? Math.round((likes / total) * 100) : 0;
  }




  getBackWarehouseDistanceText(driver: any): string {
    if (['successful', 'pending', 'unsuccessful', 'cancelled'].includes(driver?.driver_order_status)) {
      return '00.00 KM';
    }
    const rawDistance =
      driver?.drivertime?.[0]?.back_warehouse_distance ?? '0';

    const distance = typeof rawDistance === 'string'
      ? parseFloat(rawDistance)
      : Number(rawDistance);

    if (!Number.isFinite(distance) || distance < 0) {
      return '0 km away';
    }

    return `${distance.toFixed(2)} km away`;
  }
  getEstimatedReturn(driver: any): string {
    if (['successful', 'pending', 'unsuccessful', 'cancelled'].includes(driver?.driver_order_status)) {
      return 'NA';
    }
    const t = driver.drivertime?.[0]?.time_back_warehouse;
    if (!t) return 'NA';
    return new Date(t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  getLastActive(isoDate: string): string {
    if (!isoDate) return 'N/A';
    const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  }

  getStatusConfig(status: string) {
    if (['successful', 'unsuccessful', 'cancelled'].includes(status)) {
      return this.statusConfig['warehouse'];
    }
    return this.statusConfig[status] || { label: status, class: 'status-idle' };
  }

  hasCallableNumber(driver: any): boolean {
    return !!this.getDriverPhone(driver);
  }

  hasTrackLocation(driver: any): boolean {
    const location = this.getDriverLocation(driver);
    return !!location.latitude && !!location.longitude;
  }

  callDriver(driver: any, event: Event): void {
    event.stopPropagation();
    const phone = this.getDriverPhone(driver);
    if (!phone) return;

    // Toggle popover
    if (this.activeContactId === driver.id) {
      this.activeContactId = null;
    } else {
      this.activeContactId = driver.id;
    }
  }

  copyToClipboard(phone: string, event: Event, driverId: string): void {
    event.stopPropagation();
    navigator.clipboard.writeText(phone).then(() => {
      this.showCopiedId = driverId;
      setTimeout(() => {
        this.showCopiedId = null;
      }, 2000);
    });
  }

  openWhatsApp(phone: string, event: Event): void {
    event.stopPropagation();
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  }

  initiateCall(phone: string, event: Event): void {
    event.stopPropagation();
    window.location.href = `tel:${phone}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Close if click is outside any contact-trigger-wrapper
    if (!target.closest('.contact-trigger-wrapper')) {
      this.activeContactId = null;
    }
  }

  // trackDriver(driver: any): void {
  //   const location = this.getDriverLocation(driver);
  //   if (!location.latitude || !location.longitude) return;

  //   const mapsUrl = `https://maps.google.com/?q=${location.latitude},${location.longitude}`;
  //   window.open(mapsUrl, '_blank');
  // }
  trackDriver(driver: any) {
    let enterAnimationDuration = '200ms';
    let exitAnimationDuration = '200ms';

    const dialogRef = this.dialog.open(ReusableGoogleMapComponent, {
      width: '1100px',
      height: '90vh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        height: '100%', // Use 100% of dialog height
        driverData: driver,
        isComponent: true,
        isDriverDetail: true,
        showHeader: true,
        customHeaderTitle: 'Track Driver'
      },
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {

      }
    });


  }
  getDriverPhone(driver: any): string {
    const countryCode = driver?.country_code || driver?.dial_code || '';
    const phone = (
      driver?.phone_number ||
      driver?.phone ||
      driver?.mobile ||
      driver?.mobile_no ||
      driver?.drivertime?.[0]?.phone_number ||
      driver?.drivertime?.[0]?.phone ||
      ''
    ).toString().trim();

    if (countryCode && phone) {
      // Ensure country code has + prefix if it's just numbers
      const formattedCode = countryCode.toString().startsWith('+') ? countryCode : `+${countryCode}`;
      return `${formattedCode} ${phone}`;
    }
    return phone;
  }

  getDriverLocation(driver: any): { latitude: string | number; longitude: string | number } {
    return {
      latitude: driver?.drivertime?.[0]?.latitude ?? driver?.latitude ?? '',
      longitude: driver?.drivertime?.[0]?.longitude ?? driver?.longitude ?? ''
    };
  }
}
